import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  Crown,
  Loader2,
  MoreHorizontal,
  ShieldCheck,
  UserCheck,
  UserX,
} from "lucide-react";
import * as React from "react";

import {
  type AdminUser,
  type PlatformRole,
  activateUser,
  suspendUser,
  updateAdminUser,
} from "@ando/core";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
  useToast,
} from "@ando/ui";

/**
 * Vocabolario condiviso per agire su un utente: lista e dettaglio mostrano
 * lo stesso utente e devono offrire gli stessi gesti. Lo stato (dialoghi,
 * mutazioni, invalidazione) sta in ``useUserAdminActions`` cosi' le due
 * superfici non ne tengono due copie destinate a divergere.
 */

export const ROLE_TINT: Readonly<Record<string, string>> = {
  user: "text-ink-2",
  "platform-dev": "border-family-knowledge/30 bg-family-knowledge/10",
  admin: "border-family-automation/30 bg-family-automation/10",
  superadmin: "border-ando-orange/30 bg-ando-orange/10",
};

// Promuovere apre l'area admin all'utente (il gate ``isPlatformAdmin`` accetta
// role∈{admin,superadmin}): questi due passano da conferma.
const PROMOTION_ROLES: ReadonlySet<string> = new Set(["admin", "superadmin"]);

export function statusBadgeVariant(
  status: string,
): "success" | "warning" | "destructive" | "secondary" {
  switch (status) {
    case "active":
      return "success";
    case "suspended":
      return "warning";
    case "deleted":
      return "destructive";
    default:
      return "secondary";
  }
}

export const ROLE_CHOICES: readonly {
  value: PlatformRole;
  label: MessageDescriptor;
}[] = [
  { value: "user", label: msg`Utente` },
  { value: "platform-dev", label: msg`Console (dev)` },
  { value: "admin", label: msg`Admin` },
  { value: "superadmin", label: msg`Superadmin` },
];

// Icona solo sui ruoli privilegiati; spaziatore sugli altri per tenere le
// etichette incolonnate. Come ``RoleIcon`` in ``settings/OrgMembersPanel``.
export function RoleIcon({ role }: { role: PlatformRole }): React.ReactElement {
  const cls = "size-3.5 shrink-0";
  switch (role) {
    case "superadmin":
      return <Crown className={cls} aria-hidden />;
    case "admin":
      return <ShieldCheck className={cls} aria-hidden />;
    default:
      return <span className={cls} aria-hidden />;
  }
}

/** Badge del ruolo, modificabile sul posto: il valore corrente e' il
 *  trigger. Stessa forma di ``sales/LeadInlineControls``. */
export function UserRoleControl(props: {
  user: AdminUser;
  onChange: (role: PlatformRole) => void;
}): React.ReactElement {
  const { i18n } = useLingui();
  const { user } = props;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={i18n._(msg`Cambia ruolo di ${user.email}`)}
        className="inline-flex rounded-full outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <Badge
          variant="outline"
          className={cn(
            "cursor-pointer gap-1 text-3xs capitalize",
            ROLE_TINT[user.role] ?? ROLE_TINT.user,
          )}
        >
          {user.role}
          <ChevronDown size={11} strokeWidth={2.25} aria-hidden className="opacity-70" />
        </Badge>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[168px]">
        <DropdownMenuRadioGroup
          value={user.role}
          onValueChange={(v) => props.onChange(v as PlatformRole)}
        >
          {ROLE_CHOICES.map((r) => (
            <DropdownMenuRadioItem key={r.value} value={r.value} className="gap-2 text-xs">
              <RoleIcon role={r.value} />
              {i18n._(r.label)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Menu ⋯ delle azioni su un utente. ``onOpenDetail`` si passa solo dalla
 * lista: nel dettaglio la voce non ha senso, ci sei gia'.
 */
export function UserActionsMenu(props: {
  user: AdminUser;
  onOpenDetail?: () => void;
  onSuspend: () => void;
  onActivate: () => void;
}): React.ReactElement {
  const { i18n } = useLingui();
  const isSuspended = props.user.status === "suspended";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={i18n._(msg`Azioni per ${props.user.email}`)}
        className="inline-flex h-8 min-h-[36px] w-9 items-center justify-center rounded-md text-ink-2 outline-none transition-colors hover:bg-cream-deep hover:text-ink-1"
        onClick={(e) => e.stopPropagation()}
      >
        <MoreHorizontal size={14} aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {props.onOpenDetail ? (
          <>
            <DropdownMenuItem className="text-xs" onSelect={props.onOpenDetail}>
              <Trans>Apri dettaglio</Trans>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {/* Visibili e disabilitate: promemoria di cosa manca lato BE. Il
            gruppo fa anche da distanza fra la voce che si usa sempre e
            l'unica azione distruttiva. */}
        <DropdownMenuLabel>
          <Trans>In arrivo</Trans>
        </DropdownMenuLabel>
        <DropdownMenuItem disabled className="text-xs">
          <Trans>Reinvia email di verifica</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem disabled className="text-xs">
          <Trans>Resetta sessioni</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem disabled className="text-xs">
          <Trans>Forza enrolment MFA</Trans>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {isSuspended ? (
          <DropdownMenuItem className="gap-2 text-xs" onSelect={props.onActivate}>
            <UserCheck className="size-3.5 shrink-0" aria-hidden />
            <Trans>Riattiva utente</Trans>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem destructive className="gap-2 text-xs" onSelect={props.onSuspend}>
            <UserX className="size-3.5 shrink-0" aria-hidden />
            <Trans>Sospendi</Trans>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface UserAdminActions {
  /** Cambia ruolo; verso admin/superadmin passa dalla conferma. */
  requestRoleChange: (user: AdminUser, role: PlatformRole) => void;
  requestSuspend: (user: AdminUser) => void;
  requestActivate: (user: AdminUser) => void;
  /** I dialoghi da montare nella superficie che usa l'hook. */
  dialogs: React.ReactElement | null;
}

export function useUserAdminActions(): UserAdminActions {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [confirm, setConfirm] = React.useState<{
    kind: "suspend" | "activate";
    user: AdminUser;
  } | null>(null);
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [promote, setPromote] = React.useState<{
    user: AdminUser;
    role: PlatformRole;
  } | null>(null);

  const reasonOk = reason.trim().length >= 10;

  // La lista e il dettaglio leggono lo stesso utente da due chiavi diverse:
  // dopo una sospensione devono aggiornarsi entrambe, altrimenti tornando
  // indietro trovi il badge vecchio.
  const invalidate = React.useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "user"] });
  }, [queryClient]);

  const applyRoleChange = React.useCallback(
    async (user: AdminUser, role: PlatformRole): Promise<void> => {
      try {
        await updateAdminUser(user.user_id, { role });
        toast.success(i18n._(msg`Ruolo aggiornato a ${role}`));
        invalidate();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : i18n._(msg`Errore`));
      }
    },
    [i18n, invalidate, toast],
  );

  const requestRoleChange = React.useCallback(
    (user: AdminUser, role: PlatformRole): void => {
      if (role === user.role) return;
      if (PROMOTION_ROLES.has(role)) setPromote({ user, role });
      else void applyRoleChange(user, role);
    },
    [applyRoleChange],
  );

  const requestSuspend = React.useCallback((user: AdminUser): void => {
    setConfirm({ kind: "suspend", user });
    setReason("");
  }, []);

  const requestActivate = React.useCallback((user: AdminUser): void => {
    setConfirm({ kind: "activate", user });
    setReason("");
  }, []);

  async function runConfirm(): Promise<void> {
    if (!confirm) return;
    if (confirm.kind === "suspend" && !reasonOk) return;
    setBusy(true);
    try {
      if (confirm.kind === "suspend") {
        await suspendUser(confirm.user.user_id, reason.trim());
        toast.success(i18n._(msg`Utente sospeso`));
      } else {
        await activateUser(confirm.user.user_id);
        toast.success(i18n._(msg`Utente riattivato`));
      }
      invalidate();
      setConfirm(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : i18n._(msg`Errore`));
    } finally {
      setBusy(false);
    }
  }

  const dialogs =
    confirm !== null || promote !== null ? (
      <>
        {confirm ? (
          <UserActionDialog
            kind={confirm.kind}
            user={confirm.user}
            reason={reason}
            onReasonChange={setReason}
            reasonOk={reasonOk}
            busy={busy}
            onCancel={() => setConfirm(null)}
            onConfirm={() => void runConfirm()}
          />
        ) : null}
        {promote ? (
          <PromoteRoleDialog
            user={promote.user}
            role={promote.role}
            busy={busy}
            onCancel={() => setPromote(null)}
            onConfirm={() => {
              setBusy(true);
              void applyRoleChange(promote.user, promote.role).finally(() => {
                setBusy(false);
                setPromote(null);
              });
            }}
          />
        ) : null}
      </>
    ) : null;

  return { requestRoleChange, requestSuspend, requestActivate, dialogs };
}

function UserActionDialog(props: {
  kind: "suspend" | "activate";
  user: AdminUser;
  reason: string;
  onReasonChange: (v: string) => void;
  reasonOk: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  const { i18n } = useLingui();
  const requiresReason = props.kind === "suspend";

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) props.onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={16} className="shrink-0 text-accent" aria-hidden />
            {requiresReason ? i18n._(msg`Sospendi utente`) : i18n._(msg`Riattiva utente`)}
          </DialogTitle>
          <DialogDescription className="truncate">{props.user.email}</DialogDescription>
        </DialogHeader>
        {requiresReason ? (
          <label className="block text-ink-2 text-xs">
            <Trans>Motivo (≥ 10 caratteri, log audit)</Trans>
            <textarea
              value={props.reason}
              onChange={(e) => props.onReasonChange(e.target.value)}
              rows={3}
              required
              minLength={10}
              aria-label={i18n._(msg`Motivo azione amministrativa`)}
              className="mt-1.5 w-full rounded-md border border-hairline bg-cream/30 p-2 text-ink-1 text-xs focus:outline-none"
              placeholder={i18n._(msg`Es. utente segnalato per attività sospetta (#ticket-1234)`)}
            />
            <span className="mt-1 block text-3xs text-ink-3 tabular-nums">
              {props.reason.trim().length}/10
            </span>
          </label>
        ) : (
          <p className="text-ink-2 text-xs">
            <Trans>Confermi questa azione amministrativa?</Trans>
          </p>
        )}
        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={props.onCancel} disabled={props.busy}>
            <Trans>Annulla</Trans>
          </Button>
          <Button
            size="sm"
            onClick={props.onConfirm}
            disabled={props.busy || (requiresReason && !props.reasonOk)}
          >
            {props.busy ? i18n._(msg`Esecuzione…`) : i18n._(msg`Conferma`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PromoteRoleDialog(props: {
  user: AdminUser;
  role: PlatformRole;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  const { i18n } = useLingui();
  const email = props.user.email;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) props.onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {props.role === "superadmin"
              ? i18n._(msg`Promuovi a Superadmin`)
              : i18n._(msg`Promuovi ad Admin`)}
          </DialogTitle>
          <DialogDescription>
            <Trans>
              {email} otterrà accesso all'area di amministrazione della piattaforma. Puoi revocarlo
              in qualsiasi momento riportando il ruolo a Utente.
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={props.onCancel} disabled={props.busy}>
            <Trans>Annulla</Trans>
          </Button>
          <Button onClick={props.onConfirm} disabled={props.busy}>
            {props.busy ? (
              <Loader2 size={13} className="mr-1 animate-spin" aria-hidden />
            ) : (
              <ShieldCheck size={13} className="mr-1" aria-hidden />
            )}
            <Trans>Promuovi</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
